import { Link, useLocation, useNavigate } from "react-router-dom";
import usePlatformAdmin from '../hooks/usePlatformAdmin';
import { useContext, useState, useEffect, useMemo } from "react";
import { INIKRU_LOGO as inikruLogo } from '../utils/brandAssets';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  ShoppingCartIcon,
  Upload,
  CircleUserRound,
  HandHelping,
  LogOut,
  MenuIcon,
  XIcon,
  LayoutTemplate,
  Paintbrush,
  Briefcase,
  Building2,
  Combine,
  ScanBarcode,
  Package,
  Mail,
  Ticket,
  BarChart3,
  Globe,
  Layers,
  Archive,
  BellRing,
  Award,
  Gift,
  Megaphone,
  FileBarChart,
  Warehouse,
  Users,
  Inbox,
  MessageSquare,
  Hammer,
  UserCog,
  Wallet,
  CreditCard,
  Receipt,
  BookText,
  Calculator,
  Landmark,
  FolderLock,
  Share2,
  ShieldCheck,
  FileText,
  FileCheck2,
  Funnel,
  MapPin,
  ClipboardCheck,
  Rows3,
  CalendarDays,
  CalendarClock,
  PackagePlus,
  TrendingUp,
  LayoutDashboard,
  Zap,
  Settings,
  Blocks,
  Wand2,
  Tags,
  Tag,
  UsersRound,
  ChevronDown,
  Undo2,
  Truck,
} from "lucide-react";
import AuthContext from '../context/AuthContext';
import { isIndia } from '../utils/currency';
import { usePluginContext } from '../context/PluginContext';
import { useSidebarBadges } from '../context/SidebarBadgesContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import Badge from './ui/Badge';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
import { capWord, hasCapability, hasSurface, isOwner, canAccessArea, canUseTools, hasCatalogSchema, configVersion, subscribeBusinessConfig, isToolOn, managesStock } from '../utils/businessConfig';

// Short-lived cache for the subscription-status response. Base.js is rendered
// per page (not a persistent layout route), so Sidebar remounts on every
// navigation and previously refetched /get_subscription_status/ each time.
// Cache the value for a minute so a remount reuses it instead of refetching.
const SUB_CACHE_KEY = 'inikru_sub_status_cache';
const SUB_CACHE_TTL_MS = 60000;

const readSubCache = () => {
  try {
    const raw = sessionStorage.getItem(SUB_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.ts > SUB_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeSubCache = (data) => {
  try {
    sessionStorage.setItem(SUB_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // sessionStorage unavailable (private mode / quota) — skip caching.
  }
};

// Collapsed/expanded state of the sidebar section groups, persisted so the
// designer's preferred layout survives navigation and reloads. A group that
// contains the ACTIVE route is always forced open.
const COLLAPSE_KEY = 'inikru_sidebar_collapsed';
const readCollapsedGroups = () => {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY)) || {};
  } catch {
    return {};
  }
};

// Small red pill for unread / pending counts. Hidden when count is 0.
const CountBadge = ({ count }) => {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  const display = n > 99 ? '99+' : String(n);
  return (
    <span
      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold text-white bg-red-500 shadow-sm"
      aria-label={`${display} unread`}
    >
      {display}
    </span>
  );
};

export default function Sidebar() {
  const { user, authToken, logoutUser } = useContext(AuthContext);
  const {
    isPluginEnabled,
    loading: pluginsLoading,
  } = usePluginContext();
  const sidebarBadges = useSidebarBadges();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(readCollapsedGroups);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
  });

  const toggleGroup = (id) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
      return next;
    });
  };
  const location = useLocation();
  const navigate = useNavigate();

  // Memoized so we reuse one axios instance across renders/remounts instead of
  // creating a fresh `axios.create()` every render.
  const authAxios = useMemo(
    () =>
      axios.create({
        baseURL: API_BASE_URL,
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
        },
      }),
    [authToken?.access]
  );

  const fetchSubscriptionStatus = async () => {
    if (!authToken) return;
    try {
      const response = await authAxios.get('/api/get_subscription_status/');
      setSubscriptionStatus(response.data);
      writeSubCache(response.data);
    } catch (err) {
      devLog.error('Failed to fetch subscription status:', err);
      setSubscriptionStatus({ is_active: false, expiry_date: null });
      if (err.response?.status === 401) {
        Swal.fire({
          title: 'Session Expired',
          text: 'Please login again.',
          icon: 'warning',
          confirmButtonColor: "#B40A0A",
        }).then(() => {
          logoutUser();
        });
      }
    }
  };

  useEffect(() => {
    if (!authToken) return;
    // Reuse a fresh-enough cached value on remount instead of refetching.
    const cached = readSubCache();
    if (cached) {
      setSubscriptionStatus(cached);
      return;
    }
    fetchSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // The Tools page switches features in a module-level config, not React state.
  // Listen so the nav updates the moment something is switched on or off.
  const [cfgVersion, setCfgVersion] = useState(configVersion);
  useEffect(() => {
    // Compare on mount as well as subscribing — the config often lands before
    // this component exists, and a missed update leaves the wrong nav on screen.
    if (configVersion() !== cfgVersion) setCfgVersion(configVersion());
    const unsubscribe = subscribeBusinessConfig(setCfgVersion);
    const onToolsChanged = () => setCfgVersion(configVersion());
    window.addEventListener('inikru:tools-changed', onToolsChanged);
    return () => {
      unsubscribe();
      window.removeEventListener('inikru:tools-changed', onToolsChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);

  // Every item listed in the designer's plugin catalog is gated. If the
  // plugin is not installed, the sidebar entry disappears and the route
  // shows an "install required" panel instead of the page.
  const p = {
    dailyOps: isPluginEnabled('daily_ops'),
    inventory: isPluginEnabled('inventory'),
    unifiedInbox: isPluginEnabled('unified_inbox'),
    whatsapp: isPluginEnabled('whatsapp'),
    bidRequests: isPluginEnabled('bid_requests'),
    customers: isPluginEnabled('customers'),
    productionBoard: isPluginEnabled('production_board'),
    team: isPluginEnabled('team'),
    payments: isPluginEnabled('payments'),
    expenses: isPluginEnabled('expenses'),
    suppliers: isPluginEnabled('suppliers'),
    reportPnl: isPluginEnabled('report_pnl'),
    reportGstr1: isPluginEnabled('report_gstr1'),
    reportGoals: isPluginEnabled('report_goals'),
    automations: isPluginEnabled('automations'),
    emailMarketing: isPluginEnabled('email_marketing'),
  };
  const anyProductionEnabled = p.productionBoard || p.team;
  // Finance group also opens for pure-ERP businesses whose money tools are
  // CAPABILITIES (khata / CA export), not the storefront money PLUGINS.
  // Structure belongs to the platform admin; content to the designer.
  const isPlatformAdmin = usePlatformAdmin();
  const anyMoneyEnabled = (p.payments || p.expenses || p.suppliers || p.reportPnl
    || p.reportGstr1 || p.reportGoals || hasCapability('khata') || hasCapability('ca_export'))
    && canAccessArea('money');  // hidden from roles without money access (e.g. cashier)
  const anyMarketingEnabled = p.emailMarketing && hasSurface('marketing') && canAccessArea('marketing');

  const sidebarSharedClasses = "fixed inset-y-0 left-0 flex flex-col w-[260px] bg-white border-r border-line shadow-[0_20px_44px_-36px_rgba(22,19,15,0.4)] transform transition-all duration-300 ease-in-out";
  const mobileHiddenClasses = "-translate-x-full";
  const mobileVisibleClasses = "translate-x-0";
  const desktopVisibleClasses = "lg:translate-x-0 lg:shadow-none lg:border-line";

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const toggleButtonBaseClasses = "hidden fixed top-4 p-2 bg-white border border-line rounded-full text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 transition-all duration-300 ease-in-out shadow-[0_12px_28px_-18px_rgba(22,19,15,0.5)]";
  const toggleButtonClosedPosition = "left-4";
  const toggleButtonOpenPosition = "left-[200px] top-2 border-transparent shadow-none";

  const handleUploadClick = () => {
    // The trial auto-starts at registration, so a locked Upload almost always
    // means it EXPIRED — only legacy accounts may still need to start it.
    const trialUsed = !!subscriptionStatus.has_used_free_trial;
    Swal.fire({
        title: trialUsed ? 'Your free trial has ended' : 'Activate Your Free Trial',
        text: trialUsed
          ? 'Choose a plan to keep uploading products and selling on your store.'
          : 'Start your free 14-day trial to upload products and build your store. No credit card needed!',
        icon: 'info',
        confirmButtonText: trialUsed ? 'View Plans' : 'Start Free Trial',
        confirmButtonColor: "#B40A0A",
        showCancelButton: true,
        cancelButtonText: 'Later',
    }).then((result) => {
        if (result.isConfirmed) {
            navigate('/subscription');
        }
    });
  };

  const closeMenuIfOpen = () => isMobileMenuOpen && setIsMobileMenuOpen(false);

  // Skeleton placeholder while the enabled-plugins list is resolving.
  const GroupSkeleton = ({ rows = 2 }) => (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-9 bg-gray-100 rounded-[10px] animate-pulse"
        />
      ))}
    </div>
  );

  return (
    <>
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          toggleMobileMenu();
        }}
        className={`${toggleButtonBaseClasses} ${isMobileMenuOpen ? toggleButtonOpenPosition : toggleButtonClosedPosition} touch-manipulation`}
        style={{
          zIndex: 'var(--z-sidebar-toggle)',
          minHeight: '44px',
          minWidth: '44px',
          pointerEvents: 'auto'
        }}
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={isMobileMenuOpen}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isMobileMenuOpen ? <XIcon size={24} /> : <MenuIcon size={24} />}
      </motion.button>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-black/40 lg:hidden touch-manipulation"
            style={{
              zIndex: 'var(--z-modal-backdrop)',
              pointerEvents: 'auto'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>

      <motion.aside
        className={`lg:w-[270px] ${sidebarSharedClasses} ${isMobileMenuOpen ? mobileVisibleClasses : mobileHiddenClasses} ${desktopVisibleClasses}`}
        style={{ zIndex: 'var(--z-modal)', borderRadius: 0 }}
        aria-label="Sidebar"
        initial={false}
        animate={isMobileMenuOpen ? "open" : "closed"}
      >
        <div className="flex items-center justify-between h-16 px-5 border-b border-line shrink-0" style={{ borderRadius: 0 }}>
          <Link to="/homepage" className="flex items-center" onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)} aria-label="Inikru home">
            <img src={inikruLogo} alt="Inikru" className="h-[18px] w-auto block" />
          </Link>
        </div>

        <nav className="flex-grow p-4 overflow-y-auto custom-scrollbar" style={{ borderRadius: 0 }}>
          {/* ── Dashboard ── */}
          <div className="space-y-1">
            <NavItem
              icon={<HomeIcon size={20} />}
              label="Dashboard"
              to="/homepage"
              currentPath={location.pathname}
              closeMobileMenu={closeMenuIfOpen}
            />
            {pluginsLoading ? (
              <GroupSkeleton rows={1} />
            ) : (
              p.dailyOps && (
                <NavItem
                  icon={<LayoutDashboard size={20} />}
                  label="Daily Ops"
                  to="/dashboard"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                  badge={<Badge variant="success" size="sm">New</Badge>}
                />
              )
            )}
            {/* Plugin Store intentionally NOT in the nav (owner decision,
                2026-07-11) — /plugins stays reachable via PluginGuard links. */}
          </div>

          {/* ── Products & Catalog ── */}
          <NavGroup
            id="products"
            title={capWord('products', 'Products')}
            paths={["/UploadProduct", "/inventory", "/batches", "/recipes", "/item-recipes", "/serials", "/labels", "/locations", "/stock-take", "/reports/stock-ageing", "/collections-manager", "/categories", "/catalog-settings", "/my-coupons"]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              <NavItem
                icon={<Upload size={20} />}
                label={`Upload ${capWord('product', 'Product')}`}
                to="/UploadProduct"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
                disabled={!subscriptionStatus.is_active}
                onDisabledClick={handleUploadClick}
                badge={!subscriptionStatus.is_active ? <Badge variant="warning" size="sm">Pro</Badge> : null}
              />
              {pluginsLoading ? (
                <GroupSkeleton rows={1} />
              ) : (
                p.inventory && (
                  <NavItem
                    icon={<Warehouse size={20} />}
                    label="Inventory"
                    to="/inventory"
                    currentPath={location.pathname}
                    closeMobileMenu={closeMenuIfOpen}
                  />
                )
              )}
              {/* A6 — Batches & Expiry. Shown only when the business enables it. */}
              {hasCapability('batch_expiry') && (
                <NavItem
                  icon={<CalendarClock size={20} />}
                  label="Batches"
                  to="/batches"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* A8 — Recipes & yield. Shown only when the business enables it. */}
              {hasCapability('bom_yield') && (
                <NavItem
                  icon={<Combine size={20} />}
                  label="Recipes & Yield"
                  to="/recipes"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* P2.4 — Item recipes (auto-deduct ingredients on sale). */}
              {hasCapability('bom_yield') && (
                <NavItem
                  icon={<Combine size={20} />}
                  label="Item Recipes"
                  to="/item-recipes"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Serial / IMEI tracking. Shown only when the business enables it. */}
              {hasCapability('serial_tracking') && (
                <NavItem
                  icon={<ScanBarcode size={20} />}
                  label="Serials & IMEI"
                  to="/serials"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* P2.1 — Barcode labels. Shown only when the business enables it. */}
              {hasCapability('labels') && (
                <NavItem
                  icon={<Tags size={20} />}
                  label="Barcode Labels"
                  to="/labels"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Wave 2 — Multi-location stock. */}
              {hasCapability('multi_location') && (
                <NavItem
                  icon={<MapPin size={20} />}
                  label="Locations"
                  to="/locations"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Wave 2 — Stock take + auto-reorder. */}
              {hasCapability('stock_take') && (
                <NavItem
                  icon={<ClipboardCheck size={20} />}
                  label="Stock Take"
                  to="/stock-take"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* P2.5 — Stock ageing report. Inventory businesses only. */}
              {managesStock() && (
                <NavItem
                  icon={<Archive size={20} />}
                  label="Stock Ageing"
                  to="/reports/stock-ageing"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Collections = storefront merchandising — hidden for pure-ERP businesses */}
              {hasSurface('collections') && (
                <NavItem
                  icon={<Layers size={20} />}
                  label="Collections"
                  to="/collections-manager"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              <NavItem
                icon={<Tag size={20} />}
                label="Categories"
                to="/categories"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
              />
              {/* "Catalog" = fashion fabric-care + size/upload defaults. Only for
                  garment businesses on the bespoke form; a rice mill / café uses
                  its own schema catalog and has no use for fabric-care. */}
              {!hasCatalogSchema() && (
                <NavItem
                  icon={<Tags size={20} />}
                  label="Catalog"
                  to="/catalog-settings"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Coupons = storefront discounting — hidden for pure-ERP businesses */}
              {hasSurface('coupons') && (
                <NavItem
                  icon={<Ticket size={20} />}
                  label="Coupons"
                  to="/my-coupons"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Sales — mark down SOME products, undoably. Sits beside Coupons
                  because both answer "make this cheaper", and an owner should
                  not have to know which mechanism we call it to find either. */}
              {hasSurface('coupons') && (
                <NavItem
                  icon={<Tag size={20} />}
                  label="Sales"
                  to="/sales"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
          </NavGroup>

          {/* ── Orders & Customers ── */}
          <NavGroup
            id="orders"
            title="Orders"
            paths={["/Orders", "/orders-unified", "/whatsapp", "/Proposals", "/customers", "/automations", "/orderbook", "/counter", "/b2b-buyers", "/quotes", "/leads", "/order-board", "/preorders", "/appointments", "/estimates", "/credit-notes", "/challans", "/reminders", "/loyalty", "/vouchers", "/campaigns"]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              {/* A3 — Counter Billing (POS). Shown only when the business enables it. */}
              {hasCapability('counter_billing') && (
                <NavItem
                  icon={<Receipt size={20} />}
                  label="Counter Billing"
                  to="/counter"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Pre-orders by delivery day. */}
              {hasCapability('counter_billing') && (
                <NavItem
                  icon={<CalendarDays size={20} />}
                  label="Pre-orders"
                  to="/preorders"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Bookable services calendar — salons / clinics. */}
              {hasCapability('appointments') && (
                <NavItem
                  icon={<CalendarClock size={20} />}
                  label="Appointments"
                  to="/appointments"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Live kanban over counter/QR orders — placed → served. */}
              {(hasCapability('pay_later_orders') || hasCapability('counter_billing')) && (
                <NavItem
                  icon={<Rows3 size={20} />}
                  label="Order Board"
                  to="/order-board"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Online orders — only for a business that actually sells online.
                  Without this a salon that books appointments was shown an
                  empty web-orders screen it can never fill. */}
              {hasSurface('online_orders') && (
                <NavItem
                  icon={<ShoppingCartIcon size={20} />}
                  label="Orders"
                  to="/Orders"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {pluginsLoading ? (
                <GroupSkeleton rows={5} />
              ) : (
                <>
                  {p.unifiedInbox && hasSurface('online_orders') && (
                    <NavItem
                      icon={<Inbox size={20} />}
                      label="Unified Inbox"
                      to="/orders-unified"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                      badge={<CountBadge count={sidebarBadges.unifiedOrders} />}
                    />
                  )}
                  {p.whatsapp && (
                    <NavItem
                      icon={<MessageSquare size={20} />}
                      label="WhatsApp"
                      to="/whatsapp"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                      badge={<CountBadge count={sidebarBadges.whatsapp} />}
                    />
                  )}
                  {p.bidRequests && (
                    <NavItem
                      icon={<HandHelping size={20} />}
                      label="Bid Requests"
                      to="/Proposals"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                      badge={<CountBadge count={sidebarBadges.bidRequests} />}
                    />
                  )}
                  {hasSurface('storefront_design') && (
                    <NavItem
                      icon={<Mail size={20} />}
                      label="Messages"
                      to="/messages"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.customers && (
                    <NavItem
                      icon={<Users size={20} />}
                      label="Customers"
                      to="/customers"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* P3.2 — Loyalty points. */}
                  {isToolOn('loyalty') && (
                    <NavItem icon={<Award size={20} />} label="Loyalty" to="/loyalty"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {/* P3.3 — Gift vouchers. */}
                  {isToolOn('vouchers') && (
                    <NavItem icon={<Gift size={20} />} label="Gift Vouchers" to="/vouchers"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {/* P3.4 — WhatsApp campaigns. */}
                  {isToolOn('campaigns') && (
                    <NavItem icon={<Megaphone size={20} />} label="Campaigns" to="/campaigns"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {/* Wave 2 — CRM leads pipeline. Shown only when the business enables it. */}
                  {hasCapability('crm_pipeline') && (
                    <NavItem
                      icon={<Funnel size={20} />}
                      label="Leads"
                      to="/leads"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* A7 — B2B buyers. Shown only when the business enables wholesale pricing. */}
                  {hasCapability('b2b_pricing') && (
                    <NavItem
                      icon={<Building2 size={20} />}
                      label="B2B Buyers"
                      to="/b2b-buyers"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Wave 2 — B2B quotations → sales orders. */}
                  {hasCapability('b2b_pipeline') && (
                    <NavItem
                      icon={<FileText size={20} />}
                      label="Quotations"
                      to="/quotes"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* COMMERCE_DEPTH_PLAN P1 — estimates, returns, challans. */}
                  {isToolOn('estimates') && (
                    <NavItem icon={<FileText size={20} />} label="Estimates" to="/estimates"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {isToolOn('returns') && (
                    <NavItem icon={<Undo2 size={20} />} label="Returns / Credit Notes" to="/credit-notes"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {isToolOn('challans') && (
                    <NavItem icon={<Truck size={20} />} label="Delivery Challans" to="/challans"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {/* P2.3 — Price lists (wholesale / special pricing). */}
                  {hasCapability('price_lists') && (
                    <NavItem icon={<Layers size={20} />} label="Price Lists" to="/price-lists"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {/* P3.1 — Payment reminders. */}
                  {isToolOn('reminders') && (
                    <NavItem icon={<BellRing size={20} />} label="Payment Reminders" to="/reminders"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                  {p.automations && (
                    <NavItem
                      icon={<Zap size={20} />}
                      label="Automations"
                      to="/automations"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                      badge={<Badge variant="success" size="sm">New</Badge>}
                    />
                  )}
                </>
              )}
          </NavGroup>

          {/* ── Marketing ── */}
          {(pluginsLoading || anyMarketingEnabled) && (
            <NavGroup
              id="marketing"
              title="Marketing"
              paths={["/email-marketing"]}
              currentPath={location.pathname}
              collapsedMap={collapsedGroups}
              onToggle={toggleGroup}
            >
              {pluginsLoading ? (
                <GroupSkeleton rows={1} />
              ) : (
                p.emailMarketing && (
                  <NavItem
                    icon={<Mail size={20} />}
                    label="Email Marketing"
                    to="/email-marketing"
                    currentPath={location.pathname}
                    closeMobileMenu={closeMenuIfOpen}
                    badge={<Badge variant="success" size="sm">New</Badge>}
                  />
                )
              )}
            </NavGroup>
          )}

          {/* ── Production ── */}
          {(pluginsLoading || anyProductionEnabled) && (
            <NavGroup
              id="production"
              title="Production"
              paths={["/production", "/karigars"]}
              currentPath={location.pathname}
              collapsedMap={collapsedGroups}
              onToggle={toggleGroup}
            >
              {pluginsLoading ? (
                <GroupSkeleton rows={2} />
              ) : (
                <>
                  {p.productionBoard && (
                    <NavItem
                      icon={<Hammer size={20} />}
                      label="Production Board"
                      to="/production"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.team && (
                    <NavItem
                      icon={<UserCog size={20} />}
                      label="Team"
                      to="/karigars"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                </>
              )}
            </NavGroup>
          )}

          {/* ── Finance ── (everything to do with money in one place)
              Connect Payments and Earnings used to sit under Account, away from
              Expenses and the reports. Worse, they were gated only by the
              storefront surface, so they skipped the canAccessArea('money')
              check every other money screen goes through — a cashier could open
              the payouts page. Moving them here puts them behind the same
              check. */}
          {(pluginsLoading || anyMoneyEnabled
            || (hasSurface('connect_payments') && canAccessArea('money'))) && (
            <NavGroup
              id="finance"
              title="Finance"
              paths={["/connect-payments", "/earnings", "/payments", "/khata", "/daybook", "/books", "/bank-reconciliation", "/compliance", "/expenses", "/suppliers", "/purchases", "/reports/pnl", "/reports/gstr1", "/reports/goals", "/reports/commerce"]}
              currentPath={location.pathname}
              collapsedMap={collapsedGroups}
              onToggle={toggleGroup}
            >
              {pluginsLoading ? (
                <GroupSkeleton rows={3} />
              ) : (
                <>
                  {/* Getting paid, and what you were paid. */}
                  {hasSurface('connect_payments') && canAccessArea('money') && (
                    <NavItem
                      icon={<CreditCard size={20} />}
                      label="Connect Payments"
                      to="/connect-payments"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {hasSurface('connect_payments') && canAccessArea('money') && (
                    <NavItem
                      icon={<BarChart3 size={20} />}
                      label="Earnings"
                      to="/earnings"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* A4 — Customer Khata. Shown only when the business enables it. */}
                  {hasCapability('khata') && (
                    <NavItem
                      icon={<BookText size={20} />}
                      label="Khata"
                      to="/khata"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* A5 — Day Book + CA Pack. Shown only when accountant export is on. */}
                  {hasCapability('ca_export') && (
                    <NavItem
                      icon={<Receipt size={20} />}
                      label="Day Book"
                      to="/daybook"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Books — double-entry statements for the CA. Same gate as Day Book. */}
                  {hasCapability('ca_export') && (
                    <NavItem
                      icon={<Calculator size={20} />}
                      label="Books"
                      to="/books"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Bank Reconciliation — reconcile ledger to bank statement. Same gate as Books. */}
                  {hasCapability('ca_export') && (
                    <NavItem
                      icon={<Landmark size={20} />}
                      label="Bank Reconciliation"
                      to="/bank-reconciliation"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* Compliance — GSTR-3B prep + e-documents + period lock. Same gate as Books. */}
                  {hasCapability('ca_export') && (
                    <NavItem
                      icon={<FileCheck2 size={20} />}
                      label="Compliance"
                      to="/compliance"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.payments && (
                    <NavItem
                      icon={<Wallet size={20} />}
                      label="Payments"
                      to="/payments"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.expenses && (
                    <NavItem
                      icon={<Receipt size={20} />}
                      label="Expenses"
                      to="/expenses"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.suppliers && (
                    <NavItem
                      icon={<Briefcase size={20} />}
                      label="Suppliers"
                      to="/suppliers"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.suppliers && (
                    <NavItem
                      icon={<PackagePlus size={20} />}
                      label="Stock In"
                      to="/purchases"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.reportPnl && (
                    <NavItem
                      icon={<TrendingUp size={20} />}
                      label="P&L Report"
                      to="/reports/pnl"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.reportGstr1 && isIndia() && (
                    <NavItem
                      icon={<Receipt size={20} />}
                      label="GSTR-1"
                      to="/reports/gstr1"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {p.reportGoals && (
                    <NavItem
                      icon={<TrendingUp size={20} />}
                      label="Goals"
                      to="/reports/goals"
                      currentPath={location.pathname}
                      closeMobileMenu={closeMenuIfOpen}
                    />
                  )}
                  {/* P4.2 — Commerce reports (returns / loyalty / voucher / campaign). */}
                  {(isToolOn('returns') || isToolOn('loyalty') || isToolOn('vouchers') || isToolOn('campaigns')) && (
                    <NavItem icon={<FileBarChart size={20} />} label="Commerce Reports" to="/reports/commerce"
                      currentPath={location.pathname} closeMobileMenu={closeMenuIfOpen} />
                  )}
                </>
              )}
            </NavGroup>
          )}

          {/* ── Store Design ── (the public website + its analytics)
              Hidden for pure-ERP businesses AND for staff roles without
              settings access (cashier / accountant / staff). */}
          {(hasSurface('storefront_design') || hasSurface('analytics')) && canAccessArea('settings') && (
          <NavGroup
            id="store"
            title="Store"
            paths={["/design", "/template-builder", "/visual-editor", "/TemplateThemes", "/store-content", "/seo-settings", "/analytics"]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              {/* Every designer needs a way into their own editor to change
                  their words and pictures. Themes was the only door before, and
                  it is now admin-only — without this they had none. */}
              {hasSurface('storefront_design') && (
                <NavItem
                  icon={<Paintbrush size={20} />}
                  label="Website"
                  to="/design"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Themes = choosing the store's template, which is the platform
                  admin's call. Hidden rather than shown-and-refused. */}
              {isPlatformAdmin && hasSurface('storefront_design') && (
                <NavItem
                  icon={<LayoutTemplate size={20} />}
                  label="Themes"
                  to="/TemplateThemes"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Pages on/off, contact details, policies, legal wording — the
                  words the storefront says, owned by the shop not by us. */}
              {hasSurface('storefront_design') && (
                <NavItem
                  icon={<FileText size={20} />}
                  label="Pages & Content"
                  to="/store-content"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {hasSurface('storefront_design') && (
                <NavItem
                  icon={<Globe size={20} />}
                  label="SEO"
                  to="/seo-settings"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {hasSurface('analytics') && (
                <NavItem
                  icon={<BarChart3 size={20} />}
                  label="Analytics"
                  to="/analytics"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
          </NavGroup>
          )}

          {/* ── Account ── */}
          <NavGroup
            id="account"
            title="Account"
            paths={["/Profile", "/tools", "/personalize", "/store-settings", "/documents", "/portfolio", "/approvals", "/team", "/Contact", "/subscription"]}
            currentPath={location.pathname}
            collapsedMap={collapsedGroups}
            onToggle={toggleGroup}
          >
              <NavItem
                icon={<CircleUserRound size={20} />}
                label="Brand Profile"
                to="/Profile"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
              />
              {/* Portfolio / public link-hub — switchable since Jul 2026, so a
                  solo shopkeeper isn't handed a link-hub they never asked for.
                  Every EXISTING business has it on via its preset. */}
              {hasCapability('portfolio') && (
                <NavItem
                  icon={<Share2 size={20} />}
                  label="Portfolio"
                  to="/portfolio"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Tools — the owner's compose surface for the whole feature set.
                  Supersedes the Plugin Store, which only knew half the switches.
                  Beta-gated (canUseTools): hidden for everyone until
                  INIKRU_BETA_TOOLS is flipped on. */}
              {canUseTools() && isOwner() && (
                <NavItem
                  icon={<Blocks size={20} />}
                  label="Tools"
                  to="/tools"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {canUseTools() && isOwner() && (
                <NavItem
                  icon={<Wand2 size={20} />}
                  label="Make it yours"
                  to="/personalize"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Shipping, returns, COD, welcome coupon — all storefront things. */}
              {hasSurface('storefront_design') && canAccessArea('settings') && (
                <NavItem
                  icon={<Settings size={20} />}
                  label="Store Settings"
                  to="/store-settings"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Approvals — spend limits + decision queue (staff raise requests too). */}
              {hasCapability('approvals') && (
                <NavItem
                  icon={<ShieldCheck size={20} />}
                  label="Approvals"
                  to="/approvals"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Document Locker — owner/manager only (sensitive papers), and
                  switchable since Jul 2026 so it isn't forced on a new business. */}
              {hasCapability('documents') && canAccessArea('settings') && (
                <NavItem
                  icon={<FolderLock size={20} />}
                  label="Document Locker"
                  to="/documents"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              {/* Team & Roles — owner-only, and hidden until the business says
                  it actually has staff (asked at signup, switchable in Tools). */}
              {hasCapability('staff_logins') && isOwner() && (
                <NavItem
                  icon={<UsersRound size={20} />}
                  label="Team & Roles"
                  to="/team"
                  currentPath={location.pathname}
                  closeMobileMenu={closeMenuIfOpen}
                />
              )}
              <NavItem
                icon={<Mail size={20} />}
                label="Support"
                to="/Contact"
                currentPath={location.pathname}
                closeMobileMenu={closeMenuIfOpen}
              />
          </NavGroup>

        </nav>

        <div className="p-4 border-t border-line shrink-0">
          <button
            onClick={() => {
              logoutUser();
              if (isMobileMenuOpen) setIsMobileMenuOpen(false);
            }}
            className="flex items-center space-x-3 w-full px-3 py-2.5 rounded-[10px] text-gray-600 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1 focus:bg-red-50 focus:text-red-600 transition-all duration-200 group"
          >
            <LogOut size={20} className="text-gray-400 group-hover:text-red-600 transition-colors" />
            <span className="text-sm font-medium">Log out</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
}

// Collapsible sidebar section. The header toggles the group; a group whose
// `paths` contain the current route is forced open so the active item is
// never hidden.
function NavGroup({ id, title, paths = [], currentPath, collapsedMap, onToggle, children }) {
  const containsActive = paths.some((path) => currentPath === path || currentPath.startsWith(`${path}/`));
  const isCollapsed = !!collapsedMap[id] && !containsActive;
  return (
    <div className="mt-5">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={!isCollapsed}
        className="w-full flex items-center justify-between px-3 mb-2 focus:outline-none group/hdr"
      >
        <span className="font-display text-[10.5px] font-bold uppercase tracking-[0.14em] text-gray-500 group-hover/hdr:text-ink transition-colors">
          {title}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-300 group-hover/hdr:text-gray-500 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
        />
      </button>
      {!isCollapsed && <div className="space-y-1">{children}</div>}
    </div>
  );
}

function NavItem({ icon, label, to, currentPath, closeMobileMenu, disabled, onDisabledClick, badge }) {
  const isActive = currentPath === to;

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      if (onDisabledClick) {
        onDisabledClick();
      }
    } else {
      if (closeMobileMenu) closeMobileMenu();
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      aria-current={isActive ? "page" : undefined}
      className={`relative flex items-center justify-between px-3 py-2.5 rounded-[10px] transition-all duration-200 group focus:outline-none ${
        isActive
          ? 'bg-orange-50 text-primary focus:ring-2 focus:ring-primary/30 focus:ring-offset-1'
          : 'text-ink hover:bg-gray-100 hover:text-ink focus:bg-gray-100 focus:ring-2 focus:ring-gray-300 focus:ring-offset-1'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-primary"
        />
      )}
      <div className="flex items-center space-x-3">
        <span className={`transition-colors duration-200 ${
          isActive
            ? 'text-primary'
            : 'text-gray-400 group-hover:text-gray-600'
        }`}>
          {icon}
        </span>
        <span className={`text-[15px] font-medium transition-colors duration-200 ${
            isActive ? 'text-primary' : 'text-ink'
        }`}>
          {label}
        </span>
      </div>
      {badge && <span className="ml-auto">{badge}</span>}
    </Link>
  );
}
